import { CourtDayComponent } from "./court-day.component";
import { calculatePopoverLayout } from "./calendar-popover-layout";
import { presentBooking, renderCalendarEvent } from "./calendar-presentation";
import { Subscription } from "rxjs";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { FormControl, ReactiveFormsModule } from "@angular/forms";

import {
  FullCalendarModule,
  FullCalendarComponent,
} from "@fullcalendar/angular";
import { CalendarOptions, EventClickArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BookingComponent } from "./booking/booking.component";
import { BOOKING_DIALOG_CONFIG } from "./booking/booking-dialog.config";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";

import { VenueService } from "../../shared/services/venue.service";
import { CourtsService } from "../../modules/courts/courts.service";
import { BookingService } from "../bookings/booking.service";
import { useVenueWallClockForCalendar } from "app/shared/venue-wall-clock";
import { AuthService } from "app/core/auth/auth.service";
import { CourtUnavailabilityDialogComponent } from "./unavailability/court-unavailability-dialog.component";
import { CourtUnavailabilityService } from "./unavailability/court-unavailability.service";
import { CourtUnavailability } from "./unavailability/court-unavailability.types";

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

@Component({
  selector: "app-bookings",
  imports: [
    CommonModule,
    CourtDayComponent,
    MatButtonModule,
    FullCalendarModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./bookings.component.html",
  styleUrls: ["./bookings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingsComponent implements AfterViewInit, OnDestroy {
  @ViewChild(FullCalendarComponent) calendar?: FullCalendarComponent;
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private popoverObserver?: MutationObserver;
  private popoverFrame?: number;
  courtDay = false;
  visibleDate = "";
  calendarEvents: any[] = [];
  get visibleCourts() {
    return this.courts.filter(
      (c) =>
        !this.bookingFilter.courtId || c._id === this.bookingFilter.courtId,
    );
  }
  get venueTimezone() {
    return (
      this.venues.find((v) => v._id === this.selectedBooking.venueId)
        ?.timezone ||
      this.bookings[0]?.timezone ||
      "Venue local time"
    );
  }
  setView(view: string) {
    this.courtDay = view === "courts";
    this.calendarOptions = {
      ...this.calendarOptions,
      height: this.courtDay ? "auto" : "100%",
    };
    this.calendar?.getApi().changeView(this.courtDay ? "timeGridDay" : view);
  }
  jumpToDate(value: string) {
    if (value) this.calendar?.getApi().gotoDate(value);
  }
  readonly dialog = inject(MatDialog);
  venueId = new FormControl("");
  courtId = new FormControl("");
  venues: any[] = [];
  courts: any[] = [];
  bookings: any[] = [];
  private bookingsRequest?: Subscription;
  private blocksRequest?: Subscription;
  unavailabilityBlocks: CourtUnavailability[] = [];

  selectedBooking: any = {
    companyId: "",
    venueId: "",
    courtId: "",
    start: "",
    end: "",
    tz: "",
    currency: "AED",
    pricePerHour: "",
    tax: "",
    discount: "0",
    total: "0",
    playersList: [],
    paymentId: "",
    refund: "",
    status: "tentative",
    canceledAt: Date,
    cancellationReason: "",
    notes: "",
  };

  calendarOptions: CalendarOptions = {
    locale: "en-US",
    initialView: "timeGridWeek",
    firstDay: 1,
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      right: "",
    },
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    selectable: true,
    dayMaxEvents: 3,
    nowIndicator: true,
    slotEventOverlap: false,
    eventContent: renderCalendarEvent,
    eventDidMount: (arg) => {
      arg.el.title = `${arg.event.title} · ${arg.event.startStr.slice(0, 16).replace("T", " ")} · ${arg.event.extendedProps["status"] ?? arg.event.extendedProps["block"]?.reasonCode ?? ""}`;
    },
    dayHeaderFormat: { weekday: "short", month: "short", day: "numeric" },
    titleFormat: { year: "numeric", month: "short", day: "numeric" },
    views: {
      dayGridMonth: {
        dayHeaderFormat: { weekday: "short" },
        titleFormat: { year: "numeric", month: "long" },
      },
    },
    height: "100%",
    events: this.bookings,
    selectConstraint: "businessHours",
    eventConstraint: "businessHours",
    slotDuration: "0:30:00",
    slotLabelInterval: "1:00:00",
    allDaySlot: false,
    eventTimeFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
    select: this.handleSelect.bind(this),
    selectAllow: (selectInfo) => {
      const now = new Date();
      return selectInfo.start >= now;
    },
    selectOverlap: (event) =>
      event.extendedProps?.["kind"] !== "court-unavailability",
    eventClick: this.onEventClick.bind(this),
    datesSet: (arg) => {
      this.visibleDate = toDateInputValue(arg.view.currentStart);
      this.bookingFilter.startDate = arg.startStr;
      this.bookingFilter.endDate = arg.endStr;
      this.fetchBookings();
    },
  };

  bookingFilter: any = {
    venueId: "",
    courtId: "",
    startDate: "",
    endDate: "",
    status: "",
  };

  constructor(
    private venueService: VenueService,
    private courtsService: CourtsService,
    private bookingService: BookingService,
    private unavailabilityService: CourtUnavailabilityService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    this.fetchVenues();
  }

  ngAfterViewInit(): void {
    this.popoverObserver = new MutationObserver(() =>
      this.schedulePopoverLayout(),
    );
    this.popoverObserver.observe(this.hostElement.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private schedulePopoverLayout(): void {
    if (this.popoverFrame !== undefined) {
      cancelAnimationFrame(this.popoverFrame);
    }
    this.popoverFrame = requestAnimationFrame(() => {
      const host = this.hostElement.nativeElement;
      const card = host.querySelector<HTMLElement>(".schedule-card");
      if (!card) return;

      const cardRect = card.getBoundingClientRect();
      host
        .querySelectorAll<HTMLElement>(".fc-more-popover")
        .forEach((popover) => {
          const initialRect = popover.getBoundingClientRect();
          const layout = calculatePopoverLayout(
            cardRect,
            initialRect,
            document.documentElement.clientHeight,
          );
          popover.style.maxHeight = `${layout.maxHeight}px`;

          const offsetParent = popover.offsetParent as HTMLElement | null;
          if (offsetParent) {
            popover.style.top = `${layout.top - offsetParent.getBoundingClientRect().top}px`;
          }
        });
    });
  }

  fetchVenues(): void {
    this.venueService.getVenues().subscribe((venues: any) => {
      this.venues = venues?.results ?? venues ?? [];
      this.selectedBooking.venueId =
        this.venues.length > 0 ? this.venues[0]._id : "";
      this.bookingFilter.venueId = this.selectedBooking.venueId;
      this.venueId.setValue(this.selectedBooking.venueId);
      this.fetchCourts({ value: this.selectedBooking.venueId });
      this.fetchBookings();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.bookingsRequest?.unsubscribe();
    this.blocksRequest?.unsubscribe();
    this.popoverObserver?.disconnect();
    if (this.popoverFrame !== undefined) {
      cancelAnimationFrame(this.popoverFrame);
    }
  }

  fetchBookings(): void {
    this.bookingsRequest?.unsubscribe();
    this.bookingsRequest = this.bookingService
      .getBookings({ ...this.bookingFilter })
      .subscribe((bookings: any) => {
        this.bookings = (bookings.results ?? []).map(
          useVenueWallClockForCalendar,
        );
        this.updateCalendarEvents();
        this.cdr.markForCheck();
      });
    this.fetchUnavailability();
  }

  get canCreateBooking(): boolean {
    return ["admin", "manager", "staff", "super-admin"].includes(
      this.authService.getUser()?.role ?? "",
    );
  }

  get canManageUnavailability(): boolean {
    return ["admin", "manager", "staff", "super-admin"].includes(
      this.authService.getUser()?.role ?? "",
    );
  }

  private fetchUnavailability(): void {
    this.blocksRequest?.unsubscribe();
    const venueId = this.bookingFilter.venueId;
    const courtIds = this.bookingFilter.courtId
      ? [this.bookingFilter.courtId]
      : this.courts.map((court) => String(court._id));
    if (
      !venueId ||
      !this.bookingFilter.startDate ||
      !this.bookingFilter.endDate
    )
      return;
    this.blocksRequest = this.unavailabilityService
      .listForCourts(
        venueId,
        courtIds,
        this.bookingFilter.startDate,
        this.bookingFilter.endDate,
      )
      .subscribe({
        next: (blocks) => {
          this.unavailabilityBlocks = blocks;
          this.updateCalendarEvents();
          this.cdr.markForCheck();
        },
        error: () => {
          this.unavailabilityBlocks = [];
          this.updateCalendarEvents();
          this.cdr.markForCheck();
        },
      });
  }

  private updateCalendarEvents(): void {
    const courtNames = new Map(
      this.courts.map((court) => [String(court._id), court.name]),
    );
    const blockedEvents = this.unavailabilityBlocks.map((block) => ({
      id: `unavailability:${block._id}`,
      title: `${courtNames.get(String(block.courtId)) ?? "Court"}`,
      classNames: ["maintenance-event"],
      start: block.localStartDatetime,
      end: block.localEndDatetime,
      backgroundColor: "#f1f5f9",
      borderColor: "#475569",
      textColor: "#334155",
      extendedProps: { kind: "court-unavailability", block },
    }));
    this.calendarEvents = [
      ...this.bookings.map((b) => presentBooking(b, this.courts)),
      ...blockedEvents,
    ];
    this.calendarOptions = {
      ...this.calendarOptions,
      events: this.calendarEvents,
    };
  }

  private readonly DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  private normalizeVenueTiming(timing: any): Array<{
    name: string;
    checked: boolean;
    startTime: string;
    endTime: string;
  }> {
    if (!timing) return [];

    // If it arrives as JSON string
    if (typeof timing === "string") {
      try {
        timing = JSON.parse(timing);
      } catch {
        return [];
      }
    }

    if (Array.isArray(timing)) {
      return timing.filter(Boolean).map((t: any) => ({
        name: t?.name ?? "",
        checked: !!t?.checked,
        startTime: t?.startTime ?? "",
        endTime: t?.endTime ?? "",
      }));
    }

    if (typeof timing === "object") {
      return Object.keys(timing).map((day) => ({
        name: day,
        checked: !!timing[day]?.checked,
        startTime: timing[day]?.startTime ?? "",
        endTime: timing[day]?.endTime ?? "",
      }));
    }

    return [];
  }

  fetchCourts($event: any): void {
    if ($event.value) {
      const venue: any = this.venues.find((v) => v._id === $event.value) ?? {};
      this.bookingFilter.venueId = venue._id ?? "";
      this.bookingFilter.courtId = "";
      this.selectedBooking.courtId = "";

      const timingArr = this.normalizeVenueTiming(
        venue?.timing ?? venue?.businessHours ?? [],
      );

      const businessHours = timingArr
        .filter((h) => h.checked && h.startTime && h.endTime)
        .map((h) => ({
          daysOfWeek: [this.DAYS.indexOf(h.name)],
          startTime: h.startTime,
          endTime: h.endTime,
        }));

      this.calendarOptions = {
        ...this.calendarOptions,
        businessHours,
      };

      this.courtsService.getAllCourts(venue._id).subscribe((response: any) => {
        this.courts = response?.results ?? response ?? [];
        this.updateCalendarEvents();
        this.fetchUnavailability();
        this.cdr.markForCheck();
      });

      this.fetchBookings();
    } else {
      this.courts = [];
      this.selectedBooking.venueId = "";
    }
  }

  selectCourt(event: any) {
    this.selectedBooking.courtId = event.value;
    this.bookingFilter.courtId = event.value;
    this.fetchBookings();
  }
  openDialog(selectedBooking: any): void {
    const dialogRef = this.dialog.open(BookingComponent, {
      ...BOOKING_DIALOG_CONFIG,
      data: {
        booking: selectedBooking,
        courts: this.courts,
        venues: this.venues,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchBookings();
      }
    });
  }

  openUnavailabilityDialog(block?: CourtUnavailability): void {
    if (!this.selectedBooking.venueId || this.courts.length === 0) return;
    const selectedCourtId = block?.courtId || this.selectedBooking.courtId;
    const courts = selectedCourtId
      ? this.courts.filter(
          (court) => String(court._id) === String(selectedCourtId),
        )
      : this.courts;
    const dialogRef = this.dialog.open(CourtUnavailabilityDialogComponent, {
      data: {
        block,
        courts,
        venueId: this.selectedBooking.venueId,
      },
      maxWidth: "95vw",
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.fetchUnavailability();
    });
  }

  onEventClick(arg: EventClickArg) {
    const event = arg.event;
    const block = event.extendedProps?.["block"] as
      | CourtUnavailability
      | undefined;
    if (event.extendedProps?.["kind"] === "court-unavailability" && block) {
      if (this.canManageUnavailability) this.openUnavailabilityDialog(block);
      return;
    }
    this.bookingService
      .getBookingById(event.id)
      .subscribe((bookingData: any) => {
        this.openDialog(bookingData);
      });
  }

  handleSelect(selectInfo: any) {
    if (!this.canCreateBooking) return;
    if (selectInfo.courtId) this.selectedBooking.courtId = selectInfo.courtId;
    this.selectedBooking.start = selectInfo.startStr;
    this.selectedBooking.end = selectInfo.endStr;
    this.selectedBooking.allDay = selectInfo.allDay;
    this.openDialog(this.selectedBooking);
  }

  isWithinWorkingHours(day: string, time: string, hours: any[]): boolean {
    const dayInfo = hours.find((h) => h.name === day && h.checked);
    if (!dayInfo) return false;

    const [h, m] = time.split(":").map(Number);
    const [sh, sm] = dayInfo.startTime.split(":").map(Number);
    const [eh, em] = dayInfo.endTime.split(":").map(Number);

    const minutes = h * 60 + m;
    const start = sh * 60 + sm;
    const end = eh * 60 + em;

    return minutes >= start && minutes <= end;
  }
}
